import { useState } from 'react'
import { Star } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import type { Review } from './constants'

type SortOrder = 'newest' | 'highest' | 'lowest'

interface ReviewsTabProps {
  reviews: Review[]
  userNationality?: string
  /** #2115 メニュー閲覧のみの店。新しいレビューは増えない (決済が無いため) */
  menuOnly?: boolean
}

export default function ReviewsTab({ reviews, userNationality, menuOnly }: ReviewsTabProps) {
  const { t } = useI18n()
  const [showAllReviews, setShowAllReviews] = useState(true)
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [minRating4, setMinRating4] = useState(false)
  const [sameCountry, setSameCountry] = useState(false)

  const avgRating = reviews.length > 0
    ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
    : 0

  const filteredReviews = reviews
    .filter(r => !minRating4 || r.rating >= 4)
    .filter(r => !sameCountry || !userNationality || r.nationality === userNationality)

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortOrder === 'highest') return b.rating - a.rating
    if (sortOrder === 'lowest') return a.rating - b.rating
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  const INITIAL_COUNT = 5
  const displayReviews = showAllReviews ? sortedReviews : sortedReviews.slice(0, INITIAL_COUNT)
  const hasFilter = minRating4 || sameCountry

  const clearFilters = () => {
    setMinRating4(false)
    setSameCountry(false)
  }

  return (
    <div className="px-4 pt-4">
      <h2 className="font-display font-semibold text-lg text-text-primary">
        {t('storeDetail.reviews')}
      </h2>

      {/* #2115 この方式では新しいレビューは付かない。黙って空のままだと
        * 「そのうち付く」と読めてしまうので、増えないことを書いておく。 */}
      {menuOnly && (
        <p className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 font-body text-xs leading-relaxed text-text-secondary">
          {t('storeDetail.reviewsMenuOnly')}
        </p>
      )}

      {reviews.length > 0 ? (
        <>
          {/* Rating Summary */}
          <div className="flex items-center gap-3 mt-3 bg-gray-50 rounded-xl p-4">
            <p className="font-display text-3xl font-bold text-gold">
              {avgRating.toFixed(1)}
            </p>
            <div>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(avgRating) ? 'text-gold fill-gold' : 'text-gray-300 fill-none'} />
                ))}
              </div>
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {t('storeDetail.reviewCount', { count: reviews.length })}
              </p>
            </div>
          </div>

          {/* Filter Row */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1 text-xs text-text-secondary">
              <span>{t('storeDetail.reviewSortLabel')}:</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-text-primary bg-white focus:outline-none focus:border-navy"
              >
                <option value="newest">{t('storeDetail.sortNewest')}</option>
                <option value="highest">{t('storeDetail.sortHighest')}</option>
                <option value="lowest">{t('storeDetail.sortLowest')}</option>
              </select>
            </label>
            <button
              onClick={() => setMinRating4(!minRating4)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                minRating4 ? 'bg-gold text-white' : 'bg-gray-100 text-text-secondary'
              }`}
            >
              {t('storeDetail.filterMinRating')}
            </button>
            {userNationality && (
              <button
                onClick={() => setSameCountry(!sameCountry)}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  sameCountry ? 'bg-navy text-white' : 'bg-gray-100 text-text-secondary'
                }`}
              >
                <span>🌍</span>
                <span>{t('storeDetail.filterSameCountry')}</span>
              </button>
            )}
          </div>

          {/* Review List */}
          {sortedReviews.length > 0 ? (
            <>
              <div className="mt-3 space-y-3">
                {displayReviews.map((review, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-4">
                    {review.comment && (
                      <p className="font-body text-sm text-text-primary italic leading-relaxed">
                        &ldquo;{review.comment}&rdquo;
                      </p>
                    )}
                    {review.tags && review.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {review.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-yokoso-red/10 px-2 py-0.5 text-xs font-medium text-yokoso-red">
                            {t(tag)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} className={i < review.rating ? 'text-gold fill-gold' : 'text-gray-300 fill-none'} />
                      ))}
                    </div>
                    <p className="font-body text-xs text-text-secondary mt-1.5">
                      {/* #2005 国籍は未回答 ('') がありうる。そのまま並べると
                          「名前 •  • 日付」と中黒だけが浮くので、区切りごと落とす */}
                      {[
                        review.userName,
                        review.nationality,
                        new Date(review.createdAt).toLocaleDateString(),
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </p>
                  </div>
                ))}
              </div>
              {sortedReviews.length > INITIAL_COUNT && (
                <button
                  onClick={() => setShowAllReviews(!showAllReviews)}
                  className="mt-3 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors"
                >
                  {showAllReviews ? t('storeDetail.showLessReviews') : t('storeDetail.showAllReviews', { count: sortedReviews.length })}
                </button>
              )}
            </>
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 mt-3 text-center">
              <p className="text-2xl mb-1">📝</p>
              <p className="font-body text-sm text-text-secondary">{t('storeDetail.noReviewsFiltered')}</p>
              {hasFilter && (
                <button
                  onClick={clearFilters}
                  className="mt-3 text-xs text-yokoso-red font-medium underline"
                >
                  {t('storeDetail.clearFilterReviews')}
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-gray-50 rounded-xl p-6 mt-3 text-center">
          <p className="text-2xl mb-1">📝</p>
          <p className="font-body text-sm text-text-secondary">{t('storeDetail.noReviews')}</p>
        </div>
      )}
    </div>
  )
}
